/**
 * A seção da conferência facial na análise preliminar (E80.F2.T05 — ADR-0079).
 *
 * Entra logo depois das fotos do signatário: primeiro o perito escolhe o par,
 * aqui ele lê o que a comparação observou.
 *
 * Cinco estados, e cada um diz o que fazer a seguir:
 *   1. **não se aplica** — falta foto escolhida em algum grupo. Ausência
 *      declarada, nunca erro (§2.5); o botão nem aparece.
 *   2. **pronta** — o par está escolhido e a conferência ainda não foi feita.
 *   3. **em curso** — a comparação está rodando.
 *   4. **parecer emitido** — as três listas, a leitura verbal e a proveniência.
 *   5. **indisponível** — o serviço recusou; a tela diz o que houve e mantém a
 *      retentativa (E70.F2), nunca um erro técnico na frente do perito.
 *
 * A leitura verbal vem RESOLVIDA do servidor. Derivá-la aqui — "tem parecer?
 * então mostre isto" — deixaria a tela e o backend livres para discordar sobre
 * o mesmo estado, que é exatamente o que o veredito de fonte única (E59) existe
 * para impedir.
 *
 * `versaoDasFotos` é o gatilho de releitura: quando o perito troca a foto
 * escolhida na seção de cima, o par muda e o `par_mudou` deste parecer também.
 * Sem o gatilho, o aviso de troca só apareceria depois de recarregar a página.
 */
import { useCallback, useEffect, useState } from 'react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  executarConferenciaFacial,
  lerConferenciaFacial,
  type ConferenciaFacialData,
  type ConferenciaFacialParecerData,
  type FotoConferidaData,
} from '@/lib/conferencia_facial';
import { ExameEletronicoError } from '@/lib/exame_eletronico';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.exameEletronico.conferenciaFacial;
const tf = ptBR.exameEletronico.fotosDoSignatario;

const _MENSAGEM: Record<string, string> = {
  conferencia_sem_par: t.erros.conferencia_sem_par,
  foto_integridade: t.erros.foto_integridade,
  foto_indisponivel: t.erros.foto_indisponivel,
  conferencia_indisponivel: t.erros.conferencia_indisponivel,
  provider_auth: t.erros.provider_auth,
  provider_credit: t.erros.provider_credit,
  sem_chave: t.erros.sem_chave,
};

function mensagemDoErro(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _MENSAGEM[err.reason] ?? t.erros.generico;
  return t.erros.generico;
}

/** A cor da leitura acompanha o que ela afirma, e o texto nunca depende dela. */
const _TOM: Record<string, string> = {
  elementos_convergentes: 'text-accent-700',
  elementos_divergentes: 'text-fg-danger',
  nao_foi_possivel_conferir: 'text-fg-muted',
  nao_se_aplica: 'text-fg-dim',
};

interface ListaProps {
  titulo: string;
  vazio: string;
  itens: string[];
  testid: string;
}

function Lista({ titulo, vazio, itens, testid }: ListaProps): JSX.Element {
  return (
    <div data-testid={testid}>
      <h4 className="text-body-sm text-fg">{titulo}</h4>
      {itens.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{vazio}</p>
      ) : (
        <ul className="list-disc space-y-sc-1 pl-sc-4">
          {itens.map((item) => (
            <li key={item} className="max-w-prose text-body-sm text-fg-muted">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LinhaDaFoto({ foto }: { foto: FotoConferidaData }): JSX.Element {
  return (
    <li className="text-caption text-fg-dim" data-testid={`conferencia-foto-${foto.grupo}`}>
      <span className="text-fg-muted">
        {t.provenienciaLinha
          .replace('{grupo}', tf.grupos[foto.grupo].titulo)
          .replace('{arquivo}', foto.nome_arquivo)}
      </span>
      <br />
      <span className="break-all">
        {t.provenienciaCodigo.replace('{valor}', foto.content_sha256)}
      </span>
    </li>
  );
}

function Parecer({
  parecer,
  parMudou,
}: {
  parecer: ConferenciaFacialParecerData;
  parMudou: boolean;
}): JSX.Element {
  const semAfirmacao =
    parecer.leitura === 'nao_foi_possivel_conferir' &&
    parecer.convergencias.length === 0 &&
    parecer.divergencias.length === 0;

  return (
    <div className="space-y-sc-3" data-testid="conferencia-parecer" data-leitura={parecer.leitura}>
      {semAfirmacao ? (
        <p
          className="max-w-prose text-body-sm text-fg-muted"
          data-testid="conferencia-sem-afirmacao"
        >
          {t.semAfirmacao}
        </p>
      ) : (
        <>
          <Lista
            titulo={t.secoes.convergencias}
            vazio={t.vazio.convergencias}
            itens={parecer.convergencias}
            testid="conferencia-convergencias"
          />
          <Lista
            titulo={t.secoes.divergencias}
            vazio={t.vazio.divergencias}
            itens={parecer.divergencias}
            testid="conferencia-divergencias"
          />
        </>
      )}

      <Lista
        titulo={t.secoes.ressalvas}
        vazio={t.vazio.ressalvas}
        itens={parecer.ressalvas}
        testid="conferencia-ressalvas"
      />

      <div>
        <h4 className="text-body-sm text-fg">{t.provenienciaTitulo}</h4>
        <ul className="space-y-sc-1">
          <LinhaDaFoto foto={parecer.par.documento_identidade} />
          <LinhaDaFoto foto={parecer.par.ccb} />
        </ul>
        <p className="pt-sc-1 text-caption text-fg-dim">
          {t.feitoEm.replace('{data}', formatarDataHora(parecer.created_at))}
        </p>
      </div>

      {parMudou ? (
        <p
          role="status"
          className="max-w-prose text-body-sm text-fg"
          data-testid="conferencia-par-mudou"
        >
          {t.parMudou}
        </p>
      ) : null}
    </div>
  );
}

/**
 * O corpo da seção: leitura verbal, o que falta fazer e o parecer.
 *
 * Componente próprio porque a seção externa já carrega estado, efeito e
 * tratamento de erro — juntar o desenho dos cinco estados no mesmo lugar
 * estourava o limite de complexidade do lint, que aqui está fazendo o trabalho
 * dele: quem lê a tela quer saber "em que estado eu estou", e a resposta não
 * pode estar espalhada em nove ternários encadeados.
 */
function CorpoDaConferencia({ dados }: { dados: ConferenciaFacialData }): JSX.Element {
  const { leitura, parecer, tem_par_eleito: temPar } = dados;

  return (
    <>
      {leitura !== null ? (
        <p
          className={`text-body-lg ${_TOM[leitura] ?? 'text-fg'}`}
          data-testid="conferencia-leitura"
        >
          {t.leituras[leitura]}
        </p>
      ) : null}

      {!temPar && parecer === null ? (
        <p className="max-w-prose text-body-sm text-fg-muted" data-testid="conferencia-sem-par">
          {t.semParTexto}
        </p>
      ) : null}

      {parecer !== null ? <Parecer parecer={parecer} parMudou={dados.par_mudou} /> : null}

      {temPar && parecer === null ? (
        <p className="text-body-sm text-fg-muted" data-testid="conferencia-pronta">
          {t.pronta}
        </p>
      ) : null}
    </>
  );
}

/** O rótulo do botão diz o que vai acontecer, não o que está acontecendo. */
function rotuloDaAcao(conferindo: boolean, temParecer: boolean): string {
  if (conferindo) return t.conferindo;
  return temParecer ? t.refazer : t.conferir;
}

export interface ConferenciaFacialProps {
  caseId: string;
  /** O perito concluiu alguma peça: o material do exame não muda mais. */
  concluido: boolean;
  /** Muda quando as fotos do caso mudam — força a releitura do par. */
  versaoDasFotos: number;
}

export function ConferenciaFacial({
  caseId,
  concluido,
  versaoDasFotos,
}: ConferenciaFacialProps): JSX.Element {
  const client = useApiClient();
  const [dados, setDados] = useState<ConferenciaFacialData | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lido = await lerConferenciaFacial(client, caseId);
        if (!cancelado) setDados(lido);
      } catch {
        if (!cancelado) setErro(t.erros.carregar);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId, versaoDasFotos]);

  const conferir = useCallback((): void => {
    void (async () => {
      setConferindo(true);
      setErro(null);
      try {
        await executarConferenciaFacial(client, caseId);
        // Relê o estado inteiro, e não só o parecer devolvido: `par_mudou` é
        // uma comparação com o par de AGORA, e o corpo da escrita não a carrega.
        setDados(await lerConferenciaFacial(client, caseId));
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setConferindo(false);
      }
    })();
  }, [client, caseId]);

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="conferencia-facial"
      data-leitura={dados?.leitura ?? ''}
      aria-labelledby="conferencia-facial-titulo"
    >
      <div>
        <h3 id="conferencia-facial-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="conferencia-erro">
          {erro}
        </p>
      ) : null}

      {dados !== null ? <CorpoDaConferencia dados={dados} /> : null}

      {dados?.tem_par_eleito === true ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={concluido || conferindo}
          onClick={conferir}
          data-testid="conferencia-conferir"
        >
          {rotuloDaAcao(conferindo, dados.parecer !== null)}
        </Button>
      ) : null}

      <p className="max-w-prose text-caption text-fg-dim" data-testid="conferencia-regua-etica">
        {t.reguaEtica}
      </p>
    </Blueprint>
  );
}
